// src/components/shopping/OfferCard.js
import React from "react";
import {
    Card,
    CardContent,
    CardActions,
    Box,
    Typography,
    Button,
    Chip,
} from "@mui/material";
import StorefrontIcon from "@mui/icons-material/Storefront";

function normalizeQtyBase(qty, unit) {
    const v = Number(qty || 0);
    const u = (unit || "").toLowerCase();

    if (!v) return 0;

    if (["kg", "kilo", "kilogram"].includes(u)) return v * 1000; // → g
    if (["g", "gram", "grams"].includes(u)) return v;

    if (["l", "lt", "liter", "liters"].includes(u)) return v * 1000; // → ml
    if (["ml"].includes(u)) return v;

    // άγνωστη μονάδα → το αφήνουμε όπως είναι
    return v;
}

function formatPackageText(offer) {
    if (offer.package_label) return offer.package_label;
    if (offer.package_size_text) return offer.package_size_text;

    const qty = offer.normalized_qty || offer.package_normalized_qty;
    const unit = (offer.normalized_unit || offer.package_normalized_unit || "").toLowerCase();

    if (!qty || !unit) return "";

    if (unit === "ml") {
        const l = qty / 1000;
        return `${l.toFixed(2)} L`;
    }
    if (unit === "g") {
        if (qty >= 1000) {
            const kg = qty / 1000;
            return `${kg.toFixed(2)} kg`;
        }
        return `${qty.toFixed(0)} g`;
    }

    return `${qty} ${unit}`;
}

export default function OfferCard({
    offer,
    quantity,
    unit,
    lang = "gr",
    isBest,
    onSelect,
}) {
    // 1️⃣ Χαρακτηριστικά καταστήματος & προϊόντος
    const storeLabel =
        offer.store_label ||
        offer.store_name ||
        offer.store_chain ||
        offer.store ||
        (lang === "gr" || lang === "el" ? "Άγνωστο κατάστημα" : "Unknown store");

    const productLabel =
        offer.product_full_label ||
        offer.product_name ||
        offer.brand_name ||
        offer.brand_name_el ||
        offer.brand_name_en ||
        (lang === "gr" || lang === "el" ? "Άγνωστο προϊόν" : "Unknown product");

    const currencyCode = (offer.currency || "EUR").toUpperCase();
    const currencySymbol =
        currencyCode === "USD" ? "$" : currencyCode === "EUR" ? "€" : currencyCode;

    // 2️⃣ Είναι υγρό ή στερεό;
    const normUnit = (offer.unit_kind || offer.unit_price_kind || offer.normalized_unit || "").toLowerCase();
    let isLiquid =
        normUnit === "ml" ||
        normUnit === "l" ||
        normUnit === "lt";

    if (!isLiquid && productLabel) {
        const nameLower = productLabel.toLowerCase();
        if (
            nameLower.includes("ελαιόλαδο") ||
            nameLower.includes("λαδι") ||
            nameLower.includes("oil") ||
            nameLower.includes("vinegar") ||
            nameLower.includes("ξύδι")
        ) {
            isLiquid = true;
        }
    }

    const isPiece = normUnit === "piece";
    const unitSuffix = isPiece ? "/piece" : isLiquid ? "/L" : "/kg";

    // 3️⃣ Τιμή συσκευασίας
    const packagePriceRaw =
        offer.package_price ??
        offer.variant_price ??
        offer.price ??
        null;

    const packagePrice =
        packagePriceRaw != null ? Number(packagePriceRaw) : null;

    const packageText = formatPackageText(offer);

    // 4️⃣ Τιμή ανά κιλό / λίτρο (ΠΡΟΤΙΜΑΜΕ ΤΑ ΕΤΟΙΜΑ ΠΕΔΙΑ)
    let perUnit = null;

    if (isPiece) {
        if (offer.price_per_piece != null) {
            perUnit = Number(offer.price_per_piece);
        }
    } else if (isLiquid) {
        if (offer.price_per_liter != null) {
            perUnit = Number(offer.price_per_liter);
        }
    } else {
        if (offer.price_per_kg != null) {
            perUnit = Number(offer.price_per_kg);
        }
    }

    const pkgQtyBase = normalizeQtyBase(
        offer.normalized_qty || offer.package_normalized_qty,
        offer.normalized_unit || offer.package_normalized_unit || (isLiquid ? "ml" : "g")
    );

    const neededBase = normalizeQtyBase(quantity, unit);

    const totalCostBackend =
        offer.total_cost ??
        offer.basket_cost ??
        offer.theoretical_cost ??
        offer.calculated_cost ??
        null;

    let totalCost = totalCostBackend != null ? Number(totalCostBackend) : null;

    // 6️⃣ Πακέτα & περίσσευμα (αν θέλουμε να δείξουμε)
    let packagesNeeded = offer.packages_needed ?? null;
    let leftoverBase = offer.leftover_qty_g ?? null;

    if (
        (packagesNeeded == null || leftoverBase == null) &&
        pkgQtyBase > 0 &&
        neededBase > 0
    ) {
        packagesNeeded = Math.ceil(neededBase / pkgQtyBase);
        const basketQty = packagesNeeded * pkgQtyBase;
        leftoverBase = Math.max(0, basketQty - neededBase);
    }

    const leftoverText =
        leftoverBase && leftoverBase > 0
            ? (() => {
                if (isLiquid) {
                    if (leftoverBase >= 1000) {
                        const l = leftoverBase / 1000;
                        return `${l.toFixed(2)} L`;
                    }
                    return `${leftoverBase.toFixed(0)} ml`;
                } else {
                    if (leftoverBase >= 1000) {
                        const kg = leftoverBase / 1000;
                        return `${kg.toFixed(2)} kg`;
                    }
                    return `${leftoverBase.toFixed(0)} g`;
                }
            })()
            : null;

    const perUnitLabel =
        perUnit != null
            ? `${perUnit.toFixed(2)} ${currencySymbol} ${unitSuffix}`
            : lang === "gr" || lang === "el"
                ? "—"
                : "--";

    const totalCostLabel =
        totalCost != null
            ? `${totalCost.toFixed(2)} ${currencySymbol}`
            : lang === "gr" || lang === "el"
                ? "—"
                : "--";

    const packageLabelText =
        packagePrice != null
            ? `${packagePrice.toFixed(2)} ${currencySymbol}`
            : packageText || "";


    return (
        <Card
            variant={isBest ? "outlined" : "elevation"}
            sx={{
                mb: 1.5,
                borderRadius: 2,
                borderColor: isBest ? "success.main" : "divider",
            }}
        >
            <CardContent>
                {/* Πάνω σειρά: κατάστημα + best badge */}
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 0.5,
                        gap: 1,
                    }}
                >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.7 }}>
                        <StorefrontIcon sx={{ fontSize: 18 }} />
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            {storeLabel}
                        </Typography>
                    </Box>
                    {isBest && (
                        <Chip
                            size="small"
                            label={
                                lang === "gr" || lang === "el"
                                    ? "Καλύτερη επιλογή"
                                    : "Best choice"
                            }
                            color="success"
                            variant="outlined"
                        />
                    )}
                </Box>

                {/* Όνομα προϊόντος */}
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                    {productLabel}
                </Typography>

                {packageText && (
                    <Typography
                        variant="body2"
                        sx={{ mb: 0.4, color: "text.secondary", fontWeight: 500 }}
                    >
                        {packageText}
                    </Typography>
                )}

                {/* Τιμή συσκευασίας */}
                {packageLabelText && (
                    <Typography variant="body2" sx={{ mb: 0.3 }}>
                        {lang === "gr" || lang === "el"
                            ? "Τιμή συσκευασίας:"
                            : "Package price:"}{" "}
                        <strong>{packageLabelText}</strong>
                    </Typography>
                )}

                {/* Τιμή ανά μονάδα */}
                <Typography variant="body2" sx={{ mb: 0.3 }}>
                    {lang === "gr" || lang === "el"
                        ? "Τιμή ανά μονάδα:"
                        : "Unit price:"}{" "}
                    <strong>{perUnitLabel}</strong>
                </Typography>

                {/* Κόστος για την ποσότητα της συνταγής */}
                {neededBase > 0 && (
                    <Typography variant="body2">
                        {lang === "gr" || lang === "el"
                            ? "Εκτιμώμενο κόστος για την ποσότητα της λίστας:"
                            : "Estimated cost for this quantity:"}{" "}
                        <strong>{totalCostLabel}</strong>
                    </Typography>
                )}

                {/* Πακέτα & περίσσευμα */}
                {packagesNeeded && packageText && (
                    <Typography
                        variant="body2"
                        sx={{ mt: 0.3, fontSize: 12, color: "text.secondary" }}
                    >
                        {lang === "gr" || lang === "el"
                            ? `Θα χρειαστείς περίπου ${packagesNeeded} × ${packageText}`
                            : `You will need about ${packagesNeeded} × ${packageText}`}
                        {leftoverText
                            ? lang === "gr" || lang === "el"
                                ? ` (θα περισσέψει ~${leftoverText})`
                                : ` (approx. leftover ~${leftoverText})`
                            : ""}
                    </Typography>
                )}
            </CardContent>

            <CardActions sx={{ justifyContent: "flex-end", px: 2, pb: 1.5 }}>
                <Button
                    variant="contained"
                    size="small"
                    onClick={onSelect}
                    sx={{ textTransform: "none" }}
                >
                    {lang === "gr" || lang === "el"
                        ? "Επιλογή προϊόντος"
                        : "Select this product"}
                </Button>
            </CardActions>
        </Card>
    );
}
